import type { RuntimeConfig, UnityBuildConfig } from "./config";

export interface UnityInstance {
  Quit(): Promise<void>;
  SetFullscreen?(value: number): void;
  SendMessage?(gameObject: string, methodName: string, value?: string | number): void;
}

type CreateUnityInstance = (
  canvas: HTMLCanvasElement,
  config: Omit<UnityBuildConfig, "loaderUrl">,
  onProgress: (progress: number) => void,
) => Promise<UnityInstance>;

declare global {
  interface Window {
    createUnityInstance?: CreateUnityInstance;
    superWerwolfUnity?: UnityInstance;
  }
}

export function toUnityPlayerConfig(config: RuntimeConfig): Omit<UnityBuildConfig, "loaderUrl"> {
  const { loaderUrl: _loaderUrl, ...playerConfig } = config.unity;
  return playerConfig;
}

export function toUnityRuntimeMessage(config: RuntimeConfig): string {
  return JSON.stringify({
    apiBaseUrl: config.apiBaseUrl,
    environment: config.environment,
    productVersion: config.unity.productVersion,
  });
}

function loadScript(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[data-unity-loader="${CSS.escape(url)}"]`);
    if (existing?.dataset.loaded === "true") {
      resolve();
      return;
    }

    const script = existing ?? document.createElement("script");
    script.src = url;
    script.async = true;
    script.dataset.unityLoader = url;
    script.addEventListener("load", () => {
      script.dataset.loaded = "true";
      resolve();
    }, { once: true });
    script.addEventListener("error", () => reject(new Error("Unity-Loader konnte nicht geladen werden")), { once: true });
    if (!existing) document.head.append(script);
  });
}

export async function startUnity(
  canvas: HTMLCanvasElement,
  config: RuntimeConfig,
  onProgress: (progress: number) => void,
): Promise<UnityInstance> {
  await loadScript(config.unity.loaderUrl);
  if (!window.createUnityInstance) {
    throw new Error("Unity-Loader stellt createUnityInstance nicht bereit");
  }
  const instance = await window.createUnityInstance(canvas, toUnityPlayerConfig(config), onProgress);
  if (!instance.SendMessage) {
    await instance.Quit();
    throw new Error("Unity-Player stellt die Session-Brücke nicht bereit");
  }
  instance.SendMessage("SuperWerwolfSession", "ConfigureFromWeb", toUnityRuntimeMessage(config));
  window.superWerwolfUnity = instance;
  return instance;
}
